// MTFileUploadSection - Multi-territory file upload form
// Mirrors India's FileUploadSection.jsx but uses MT API service + TypeScript
import alertFilledIcon from '@/assets/icons/alert-filled.svg'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  styled,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import CustomSelect from '@/components/Returns/CustomSelect'
import CommonModal from '@/components/common/CommonModal'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { triggerBlobDownload } from '@/multi-territory/mt-services/mt-service-master-api'
import {
  downloadMTTemplate,
  fetchMTFileDataType,
  fetchMTFileSource,
  uploadMTFile,
} from '@/multi-territory/mt-services/mt-upload-api'
import type {
  MTUploadDataType,
  MTUploadSource,
} from '@/multi-territory/mt-types/mt-upload'
import { invalidateMTPeriodOptions } from '@/store/slice/mtEntityTanSlice'

const StyledPaper = styled(Paper)(({ theme }) => ({
  border: '2px dashed #AAAAAA',
  borderRadius: '8px',
  padding: theme.spacing(1.5),
  backgroundColor: '#ebebeb',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': { borderColor: '#999', backgroundColor: '#f5f5f5' },
  '&.dragover': { borderColor: '#ebebeb', backgroundColor: '#ebebeb' },
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  maxHeight: '250px',
  overflowY: 'auto',
  width: '100%',
}))

const isAltSource = (source: MTUploadSource | null) => {
  return source?.value?.toLowerCase().startsWith('alt-')
}

type MTFileUploadSectionProps = {
  fetchMetaData: () => void
  onSourcesLoaded?: (sources: MTUploadSource[]) => void
  onDataTypesLoaded?: (types: MTUploadDataType[]) => void
}

export function MTFileUploadSection({
  fetchMetaData,
  onSourcesLoaded,
  onDataTypesLoaded,
}: MTFileUploadSectionProps) {
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country?.countryCode
  )
  const dispatch = useDispatch()
  const { showSnackbar } = useSnackbar() as any

  const [source, setSource] = useState<MTUploadSource | null>(null)
  const [dataType, setDataType] = useState<MTUploadDataType | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [sources, setSources] = useState<MTUploadSource[]>([])
  const [dataTypes, setDataTypes] = useState<MTUploadDataType[]>([])
  const [isDownloading, setIsDownloading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)

  useEffect(() => {
    const loadEnums = async () => {
      try {
        const [sourceRes, typeRes] = await Promise.all([
          fetchMTFileSource(countryCode),
          fetchMTFileDataType(countryCode),
        ])
        console.log(sourceRes, typeRes, 'typ source')

        if (sourceRes.success) {
          setSources(sourceRes.data)
          onSourcesLoaded?.(sourceRes.data)
          if (sourceRes.data.length > 0) {
            setSource(
              sourceRes.data.find((s: MTUploadSource) => s.id === 0) ||
                sourceRes.data[0]
            )
          }
        }
        if (typeRes.success) {
          setDataTypes(typeRes.data)
          onDataTypesLoaded?.(typeRes.data)
          if (typeRes.data.length > 0) setDataType(typeRes.data[0])
        }
      } catch (err) {
        console.error('Error fetching upload enums:', err)
      }
    }
    if (countryCode) loadEnums()
  }, [countryCode])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    handleFilesValidation(Array.from(e.dataTransfer.files))
  }
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFilesValidation(Array.from(e.target.files))
  }

  const handleFilesValidation = (files: File[]) => {
    if (isAltSource(source)) {
      const newExcelFiles = files.filter(
        f =>
          f.name.toLowerCase().endsWith('.xls') ||
          f.name.toLowerCase().endsWith('.xlsx')
      )
      if (newExcelFiles.length === 0) {
        showSnackbar('Please select Excel files (.xls or .xlsx)', 'error')
        return
      }
      const existingNames = new Set(
        selectedFiles.map(f => f.name.toLowerCase())
      )
      const uniqueNewFiles = newExcelFiles.filter(
        f => !existingNames.has(f.name.toLowerCase())
      )
      setSelectedFiles(prev => [...prev, ...uniqueNewFiles])
      return
    }
    const newExcelFiles = files.filter(
      f =>
        f.name.toLowerCase().endsWith('.xls') ||
        f.name.toLowerCase().endsWith('.xlsx')
    )
    if (newExcelFiles.length > 1) {
      showSnackbar('Only one Excel file can be uploaded', 'error')
      return
    }
    const existingExcel = selectedFiles.some(
      f =>
        f.name.toLowerCase().endsWith('.xls') ||
        f.name.toLowerCase().endsWith('.xlsx')
    )
    if (existingExcel && newExcelFiles.length > 0) {
      showSnackbar('Only one Excel file can be uploaded', 'error')
      return
    }
    if (newExcelFiles.length === 1) setSelectedFiles([newExcelFiles[0]])
  }

  const handleBrowseFiles = () => {
    const fileInput = document.getElementById(
      'mt-file-input'
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
      fileInput.click()
    }
  }

  const handleDownloadTemplate = async () => {
    if (!dataType?.value) return
    try {
      setIsDownloading(true)
      const result = await downloadMTTemplate(countryCode, dataType.value)
      if (result.success) {
        triggerBlobDownload(result.data, `${dataType.value}_template.xlsx`)
        showSnackbar('Template downloaded successfully', 'success')
      } else {
        showSnackbar(result.error || 'Download failed', 'error')
      }
    } catch (err: any) {
      showSnackbar(err?.message || 'Download failed', 'error')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleUploadClick = async () => {
    if (!selectedFiles.length) {
      showSnackbar('Please select a file to upload', 'error')
      return
    }
    if (!source?.value || !dataType?.value) {
      showSnackbar('Please select source and data type', 'error')
      return
    }
    try {
      setIsUploading(true)
      showSnackbar('Uploading files...', 'info')
      const result = await uploadMTFile(countryCode, {
        file: isAltSource(source) ? selectedFiles : selectedFiles[0],
        source: source.value,
        dataType: dataType.value,
        category: dataType.category,
        loadType: 'Web Based',
        transformId: source.id,
      })
      if (result.success) {
        fetchMetaData()
        // The upload may introduce a new year/period. Invalidate the cached
        // period options so the Prepare/Fetch year dropdown re-fetches from the
        // API instead of showing stale Redux data (previously only refreshed on
        // a full page reload).
        dispatch(invalidateMTPeriodOptions())
        showSnackbar('File uploaded successfully', 'success')
        setSelectedFiles([])
      } else {
        showSnackbar(result.error || 'Upload failed', 'error')
      }
    } catch (err: any) {
      showSnackbar(err?.message || 'Upload failed', 'error')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveFile = (fileToRemove: File, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    setSelectedFiles(prev => prev.filter(f => f !== fileToRemove))
  }

  return (
    <Box sx={{ p: '16px 24px', background: '#ffffff' }}>
      <Box className="flex pb-4 justify-start gap-5 items-center">
        <Typography
          variant="h5"
          sx={{
            fontSize: '18px',
            fontWeight: 'normal',
            lineHeight: '22px',
            color: 'var(--forbrand-secondary)',
          }}
        >
          Upload
        </Typography>
        <Alert
          severity="warning"
          icon={
            <img
              src={alertFilledIcon}
              alt=""
              width={16}
              height={16}
              style={{ display: 'block' }}
            />
          }
          sx={{
            fontSize: '16px',
            background: '#FCFAEA',
            borderRadius: '4px',
            color: 'var(--forground-primary)',
            py: 0,
            display: 'inline-flex',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              alignItems: 'center',
              py: 0,
            },
            '& .MuiAlert-message': {
              py: '12px',
            },
          }}
        >
          Please do not upload more than 2,00,000 line items in a single file.
        </Alert>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <StyledPaper
            className={isDragOver ? 'dragover' : ''}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleBrowseFiles}
            sx={{
              height: '50px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '50px',
                overflow: 'hidden',
              }}
            >
              {selectedFiles.length ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      backgroundColor: '#f4f4f4',
                      borderRadius: '4px',
                      px: 1,
                      py: 0.3,
                      flexShrink: 0,
                      maxWidth: '220px',
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{
                        fontSize: '13px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={selectedFiles[0].name}
                    >
                      {selectedFiles[0].name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        handleRemoveFile(selectedFiles[0], e)
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {selectedFiles.length > 1 && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Chip
                        label={`+${selectedFiles.length - 1} more`}
                        size="small"
                        sx={{
                          fontSize: '12px',
                          backgroundColor: '#e0e0e0',
                          height: '24px',
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation()
                          setViewModalOpen(true)
                        }}
                        sx={{ color: '#D93954' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" sx={{ fontSize: '14px' }}>
                  Drag & drop files in this area or
                  <Typography
                    component="span"
                    sx={{
                      color: '#d93954',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      pl: 1,
                      fontSize: '14px',
                    }}
                  >
                    Browse Files
                  </Typography>
                </Typography>
              )}
            </Box>
          </StyledPaper>
          <input
            id="mt-file-input"
            type="file"
            hidden
            onChange={handleFileSelect}
            accept={isAltSource(source) ? '.xlsx,.xls' : '.csv,.xlsx,.xls'}
            multiple={!!isAltSource(source)}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <CustomSelect
            label=""
            value={source?.value || ''}
            onChange={(e: any) => {
              const selected = sources.find(s => s.value === e.target.value)
              setSource(selected || null)
              setSelectedFiles([])
            }}
            options={sources as any}
            getOptionLabel={(opt: any) => opt.value}
            getOptionValue={(opt: any) => opt.value}
            placeholder="Select Source"
          />
          <CustomSelect
            label=""
            value={dataType?.value || ''}
            onChange={(e: any) => {
              const selected = dataTypes.find(dt => dt.value === e.target.value)
              setDataType(selected || null)
            }}
            options={dataTypes as any}
            getOptionLabel={(opt: any) => opt.label}
            getOptionValue={(opt: any) => opt.value}
            placeholder="Select Data Type"
          />
          <Button
            variant="text"
            sx={{
              fontSize: '14px',
              textTransform: 'none',
              color: 'var(--forbrand-primary)',
            }}
            onClick={handleDownloadTemplate}
            disabled={isDownloading || !dataType?.value}
          >
            Download Template
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: 'none',
              height: '30px',
              p: '8px 20px',
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
            }}
            onClick={handleUploadClick}
            disabled={!selectedFiles.length || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      </Box>
      <CommonModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title={`Selected Files (${selectedFiles.length})`}
        maxWidth="sm"
        fullWidth
        onNext={undefined}
      >
        <List dense>
          {selectedFiles.map((file, index) => (
            <ListItem
              key={`${file.name}-${index}`}
              sx={{
                borderBottom: '1px solid #f0f0f0',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <ListItemText
                primary={file.name}
                secondary={`${(file.size / 1024).toFixed(1)} KB`}
                primaryTypographyProps={{ fontSize: '14px' }}
                secondaryTypographyProps={{ fontSize: '12px' }}
              />
              <IconButton
                edge="end"
                size="small"
                onClick={() => {
                  setSelectedFiles(prev => {
                    const updated = prev.filter(f => f !== file)
                    if (updated.length === 0) setViewModalOpen(false)
                    return updated
                  })
                }}
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </ListItem>
          ))}
        </List>
      </CommonModal>
    </Box>
  )
}

export default MTFileUploadSection
